import { observer } from "mobx-react-lite"

import type { ILiquidityPool } from "@/domains/pools/types"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { Table, TableBody, TableCell, TableHead, TableHeaderMinimal, TableRow } from "@/components/table"
import { DisplayTokenRow } from "@/domains/common/components/display-token-row"
import { cn, dollars } from "@/domains/library"
import { DisplayLiquidityPool } from "@/domains/pools"
import { TokenStore } from "@/domains/tokens"
import useResponsive from "@/hooks/use-responsive"
import { showDisplayType } from "@/lib/show-display-type"

export type FarmingRewardsViewProps = {
  title: string
  description: string
  poolType: "V2" | "CL"
  governanceToken: TokenStore
  pools: Array<
    ILiquidityPool & {
      onChainUserGaugeGovernanceEarned: bigint
      onChainUserGaugeReserve0: bigint
      onChainUserGaugeReserve1: bigint
      gaugeAddress?: { toViemAddress: () => string } | null
      hasGauge?: boolean
    }
  >
  onClaimAllLpRewards: (pools: Array<ILiquidityPool>) => void
  onClaimLpReward: (pool: ILiquidityPool) => void
  emptyState: React.ReactNode
}

export const FarmingRewardsView = observer(
  ({
    title,
    description,
    poolType,
    governanceToken,
    pools,
    onClaimAllLpRewards,
    onClaimLpReward,
    emptyState,
  }: FarmingRewardsViewProps) => {
    const { isMobile, isTablet, isDesktop } = useResponsive()

    const hasRewards = pools.length > 0

    // Calculate rewards value for each pool (convert onChainUserGaugeGovernanceEarned to number only when displaying)
    const poolsWithValues = pools.map((pool) => {
      const rewardAmount = governanceToken.formatUnits(pool.onChainUserGaugeGovernanceEarned)
      const rewardsValueUSD = governanceToken.calculateValueUSD(Number(rewardAmount))
      return {
        pool,
        rewardsValueUSD,
      }
    })

    // Calculate total rewards value
    const totalFarmingRewardsValueUSD = poolsWithValues.reduce((sum, { rewardsValueUSD }) => sum + rewardsValueUSD, 0)

    const handleClaimReward = (pool: ILiquidityPool) => {
      void onClaimLpReward(pool)
    }

    // Check if pool has gauge (for position display)
    const hasGauge = (
      pool: ILiquidityPool & {
        gaugeAddress?: { toViemAddress: () => string } | null
        hasGauge?: boolean
      },
    ) => {
      return pool.hasGauge ?? (pool.gaugeAddress !== null && pool.gaugeAddress !== undefined)
    }

    return (
      <Card.Container className="flex w-full flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
        <div className="flex flex-row items-center justify-between gap-2">
          <div>
            <Card.HeaderTitle>{title}</Card.HeaderTitle>
            <Card.HeaderDescription>{description}</Card.HeaderDescription>
          </div>
          <ShowWhenTrue when={hasRewards}>
            <div className="flex items-center gap-4">
              <div className="hidden text-right md:block">
                <div className="text-sm text-t200">Total Rewards</div>
                <div className="text-lg font-medium text-t100">
                  {dollars(Math.ceil(totalFarmingRewardsValueUSD * 100) / 100)}
                </div>
              </div>
              <Button
                onClick={() => void onClaimAllLpRewards(pools)}
                className="min-w-[148px] whitespace-nowrap md:min-w-fit"
                variant="primary"
              >
                Claim All
              </Button>
            </div>
          </ShowWhenTrue>
        </div>

        <ShowWhenTrue when={hasRewards && isMobile}>
          <div className="overflow-hidden rounded-t-xl border border-p400-light ">
            <div className={cn("bg-p400 px-4 text-t200", "items-center justify-between gap-2 py-2 pr-8 text-sm")}>
              Pool / Earned
            </div>

            {poolsWithValues.map(({ pool, rewardsValueUSD }) => {
              return (
                <div
                  key={pool.address.toString()}
                  className={cn("flex flex-col gap-2 border-b border-p400-light bg-p400 px-4 py-4 text-sm text-t200")}
                >
                  <div className="flex items-start justify-between">
                    <DisplayLiquidityPool pool={pool} label={showDisplayType(pool.displayType, isMobile, isTablet)} />
                    <span className="text-t100">{pool.displayApr}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="space-y-1">
                      <ShowWhenTrue when={rewardsValueUSD > 0}>
                        <DisplayTokenRow token={governanceToken} amount={pool.onChainUserGaugeGovernanceEarned} />
                      </ShowWhenTrue>
                    </div>

                    <div className="">
                      <Button title="Claim fees" onClick={() => handleClaimReward(pool)} variant="percentage">
                        Claim
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </ShowWhenTrue>

        <ShowWhenTrue when={hasRewards && (isTablet || isDesktop)}>
          <div className="overflow-hidden rounded-t-xl border border-p400-light">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderMinimal className="text-left" isSortDisabled={true}>
                    Pool
                  </TableHeaderMinimal>
                  <TableHeaderMinimal className="text-right" isSortDisabled={true}>
                    APR
                  </TableHeaderMinimal>
                  <TableHeaderMinimal className="hidden text-right lg:block" isSortDisabled={true}>
                    Your Position
                  </TableHeaderMinimal>
                  <TableHeaderMinimal className="text-right" isSortDisabled={true}>
                    Earned
                  </TableHeaderMinimal>
                  <TableHeaderMinimal className="text-center" isSortDisabled={true}>
                    Total
                  </TableHeaderMinimal>
                  <TableHeaderMinimal className="text-center" isSortDisabled={true}>
                    Action
                  </TableHeaderMinimal>
                </TableRow>
              </TableHead>
              <TableBody>
                {poolsWithValues.map(({ pool, rewardsValueUSD }, index) => {
                  const isFirstItem = index === 0
                  const poolHasGauge = hasGauge(pool)

                  return (
                    <TableRow
                      className={cn(!isFirstItem && "border-t border-t-p400-light")}
                      key={pool.address.toString()}
                    >
                      <TableCell>
                        <div className="flex items-center">
                          <div
                            className={cn(
                              "mr-2 flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-medium",
                              "bg-secondary-400/20 text-secondary-400",
                            )}
                          >
                            {poolType}
                          </div>
                          <DisplayLiquidityPool
                            pool={pool}
                            label={showDisplayType(pool.displayType, isMobile, isTablet)}
                          />
                        </div>
                      </TableCell>

                      <TableCell className="text-right text-xs text-t100">
                        {pool.displayApr}
                      </TableCell>

                      <TableCell className="hidden space-y-1 text-xs lg:block">
                        <ShowWhenTrue when={poolHasGauge && Boolean(pool.tokens[0] && pool.tokens[1])}>
                          <DisplayTokenRow token={pool.tokens[0]!} amount={pool.onChainUserGaugeReserve0} />
                          <DisplayTokenRow token={pool.tokens[1]!} amount={pool.onChainUserGaugeReserve1} />
                        </ShowWhenTrue>
                      </TableCell>

                      <TableCell className="text-xs text-t100">
                        <ShowWhenTrue when={pool.onChainUserGaugeGovernanceEarned > 0n}>
                          <DisplayTokenRow token={governanceToken} amount={pool.onChainUserGaugeGovernanceEarned} />
                        </ShowWhenTrue>
                      </TableCell>

                      <TableCell className="">
                        <div className="mx-auto flex w-fit gap-1 rounded-lg border-secondary-300/50 bg-secondary-400/[0.12] px-2 py-2 text-xs text-t100">
                          {dollars(rewardsValueUSD)}
                        </div>
                      </TableCell>

                      <TableCell className="content-center">
                        <Button
                          title="Claim fees"
                          onClick={() => handleClaimReward(pool)}
                          variant="percentage"
                          className="mx-auto"
                        >
                          Claim
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </ShowWhenTrue>

        <ShowWhenTrue when={!hasRewards}>{emptyState}</ShowWhenTrue>
      </Card.Container>
    )
  },
)
